import type { IReservationTableRef } from '@/interfaces/IReservation';
import type { TTableReservationsMode } from '@/interfaces/IReservationSettings';

/**
 * The tables draft of the setup guide and the Ajustes tables card. The draft
 * is an OVERLAY: only tables the user (or the guide seed) touched have an
 * entry, so a change made elsewhere to an untouched table is never overwritten
 * and never marks the draft dirty. Every function returns a new object.
 */
export interface IReservationTableDraftValues {
  capacity: number;
  reservationsMode: TTableReservationsMode;
}

/** OVERLAY: only tables the user (or the guide seed) touched have an entry. */
export type TReservationTablesDraftValues = Readonly<
  Record<number, IReservationTableDraftValues>
>;

/** One row of `PUT /reservations/tables`; only the fields that change. */
export interface IReservationTableBatchEntry {
  tableId: number;
  capacity?: number;
  mode?: TTableReservationsMode;
}

export const TABLE_SEATS_MIN = 1;
export const TABLE_SEATS_MAX = 50;
/** The API bound (`PUT /reservations/tables`); a legacy row above it only ever moves down. */
export const TABLE_SEATS_API_MAX = 200;
export const DEFAULT_TABLE_SEATS = 4;
export const MAX_CREATED_TABLES = 100;

/** Delivery, counter and cashier rows are not tables anyone is seated at. */
export const NON_BOOKABLE_TABLE_NAME = /\b(delivery|take ?away|para llevar|retiro|mostrador|barra|caja|pedidos ?ya|rappi)\b/i;

export function looksNonBookableTable(name: string): boolean {
  return NON_BOOKABLE_TABLE_NAME.test(name);
}

export interface ISelectionSummary {
  count: number;
  /** Common seats of the selection; null when mixed or when every table is off. */
  capacity: number | null;
  allOff: boolean;
  anyOff: boolean;
  names: string[];
}

export type TPlanTileTone =
  | 'default'
  | 'internal'
  | 'none'
  | 'seatless'
  | 'grouped'
  | 'inactive';

/** 1..50; a non-number (an emptied input) counts as the minimum. */
function clampSeats(capacity: number): number {
  if (!Number.isFinite(capacity)) return TABLE_SEATS_MIN;
  return Math.min(
    TABLE_SEATS_MAX,
    Math.max(TABLE_SEATS_MIN, Math.trunc(capacity))
  );
}

function byId(
  tables: readonly IReservationTableRef[]
): Map<number, IReservationTableRef> {
  return new Map(tables.map((table) => [table.id, table]));
}

/** The server row merged with the overlay entry, when there is one. */
function effectiveValues(
  table: IReservationTableRef,
  values: TReservationTablesDraftValues
): IReservationTableDraftValues {
  const entry = values[table.id];
  return entry
    ? { capacity: entry.capacity, reservationsMode: entry.reservationsMode }
    : { capacity: table.capacity, reservationsMode: table.reservationsMode };
}

function sameAsRow(
  table: IReservationTableRef,
  entry: IReservationTableDraftValues
): boolean {
  return (
    entry.capacity === table.capacity &&
    entry.reservationsMode === table.reservationsMode
  );
}

function isOff(mode: TTableReservationsMode): boolean {
  return mode !== 'online';
}

/**
 * D12: seatless online tables get the default seats; the ones whose name
 * looks like delivery or a counter stop being reservable instead and are
 * reported so the guide can list them in an undoable note.
 */
export function seedTablesDraft(
  tables: readonly IReservationTableRef[],
  defaultSeats: number
): { values: TReservationTablesDraftValues; excludedIds: number[] } {
  const values: Record<number, IReservationTableDraftValues> = {};
  const excludedIds: number[] = [];
  for (const table of tables) {
    if (
      !table.enabled ||
      table.reservationsMode !== 'online' ||
      table.capacity > 0
    )
      continue;
    if (looksNonBookableTable(table.name)) {
      values[table.id] = { capacity: 0, reservationsMode: 'none' };
      excludedIds.push(table.id);
    } else {
      values[table.id] = {
        capacity: clampSeats(defaultSeats),
        reservationsMode: 'online',
      };
    }
  }
  return { values, excludedIds };
}

/** New objects only for tables with an entry; untouched rows are returned as is. */
export function applyTablesDraft(
  tables: readonly IReservationTableRef[],
  values: TReservationTablesDraftValues
): IReservationTableRef[] {
  return tables.map((table) => {
    const entry = values[table.id];
    return entry
      ? {
          ...table,
          capacity: entry.capacity,
          reservationsMode: entry.reservationsMode,
        }
      : table;
  });
}

/** Gives the tables these seats (clamped to 1..50) and makes them online. */
export function withSeats(
  tables: readonly IReservationTableRef[],
  values: TReservationTablesDraftValues,
  ids: readonly number[],
  capacity: number
): TReservationTablesDraftValues {
  const known = byId(tables);
  const next: Record<number, IReservationTableDraftValues> = { ...values };
  const seats = clampSeats(capacity);
  for (const id of ids) {
    if (!known.has(id)) continue;
    next[id] = { capacity: seats, reservationsMode: 'online' };
  }
  return next;
}

/**
 * D15: an online table moves by the delta (a stored value above 50 is kept
 * as its own ceiling; never below 1); an off table only turns online with
 * the seats it already had (or 1), without moving.
 */
export function adjustSeats(
  tables: readonly IReservationTableRef[],
  values: TReservationTablesDraftValues,
  ids: readonly number[],
  delta: number
): TReservationTablesDraftValues {
  const known = byId(tables);
  const step = Number.isFinite(delta) ? Math.trunc(delta) : 0;
  if (step === 0) return values;
  const next: Record<number, IReservationTableDraftValues> = { ...values };
  for (const id of ids) {
    const table = known.get(id);
    if (!table) continue;
    const current = effectiveValues(table, values);
    if (isOff(current.reservationsMode)) {
      next[id] = {
        capacity: Math.max(TABLE_SEATS_MIN, current.capacity),
        reservationsMode: 'online',
      };
      continue;
    }
    const capacity = Math.min(
      TABLE_SEATS_API_MAX,
      step > 0
        ? Math.min(
            Math.max(TABLE_SEATS_MIN, current.capacity + step),
            Math.max(TABLE_SEATS_MAX, current.capacity)
          )
        : Math.max(TABLE_SEATS_MIN, current.capacity + step)
    );
    next[id] = { capacity, reservationsMode: 'online' };
  }
  return next;
}

/** Sets the mode; a table turned online without seats gets `defaultSeats`. */
export function withMode(
  tables: readonly IReservationTableRef[],
  values: TReservationTablesDraftValues,
  ids: readonly number[],
  mode: TTableReservationsMode,
  defaultSeats: number
): TReservationTablesDraftValues {
  const known = byId(tables);
  const next: Record<number, IReservationTableDraftValues> = { ...values };
  for (const id of ids) {
    const table = known.get(id);
    if (!table) continue;
    const current = effectiveValues(table, values);
    next[id] = {
      capacity:
        mode === 'online' && current.capacity <= 0
          ? clampSeats(defaultSeats)
          : current.capacity,
      reservationsMode: mode,
    };
  }
  return next;
}

/** «Poner en todas»: every enabled table whose effective mode is online. */
export function withSeatsForOnline(
  tables: readonly IReservationTableRef[],
  values: TReservationTablesDraftValues,
  capacity: number
): TReservationTablesDraftValues {
  const ids = tables
    .filter(
      (table) =>
        table.enabled &&
        effectiveValues(table, values).reservationsMode === 'online'
    )
    .map((table) => table.id);
  return withSeats(tables, values, ids, capacity);
}

/** Overlay entries that differ from the server row, in table order. */
export function dirtyTableIds(
  tables: readonly IReservationTableRef[],
  values: TReservationTablesDraftValues
): number[] {
  const ids: number[] = [];
  for (const table of tables) {
    const entry = values[table.id];
    if (entry && !sameAsRow(table, entry)) ids.push(table.id);
  }
  return ids;
}

/**
 * Per dirty table, only the fields whose draft value differs from the server
 * row (`capacity` omitted when 0 or equal, `mode` omitted when equal), so a
 * mode-only toggle never overwrites seats another admin gave meanwhile. A
 * dirty entry always yields at least one field.
 */
export function tablesBatchEntries(
  tables: readonly IReservationTableRef[],
  values: TReservationTablesDraftValues,
  ids: readonly number[]
): IReservationTableBatchEntry[] {
  const known = byId(tables);
  const entries: IReservationTableBatchEntry[] = [];
  for (const id of ids) {
    const table = known.get(id);
    const entry = values[id];
    if (!table || !entry || sameAsRow(table, entry)) continue;
    const row: IReservationTableBatchEntry = { tableId: id };
    if (entry.capacity !== table.capacity && entry.capacity > 0)
      row.capacity = entry.capacity;
    if (entry.reservationsMode !== table.reservationsMode)
      row.mode = entry.reservationsMode;
    // Only seats going to 0 reach here: the API needs a field, so restate the mode.
    if (row.capacity === undefined && row.mode === undefined)
      row.mode = entry.reservationsMode;
    entries.push(row);
  }
  return entries;
}

/** Drops entries equal to the server row (and entries of tables that no longer exist). */
export function pruneTablesDraft(
  tables: readonly IReservationTableRef[],
  values: TReservationTablesDraftValues
): TReservationTablesDraftValues {
  const next: Record<number, IReservationTableDraftValues> = {};
  for (const table of tables) {
    const entry = values[table.id];
    if (entry && !sameAsRow(table, entry)) next[table.id] = entry;
  }
  return next;
}

export function selectionSummary(
  selection: readonly IReservationTableRef[]
): ISelectionSummary {
  const count = selection.length;
  const allOff =
    count > 0 && selection.every((table) => isOff(table.reservationsMode));
  const anyOff = selection.some((table) => isOff(table.reservationsMode));
  const first = selection[0];
  const uniform =
    !!first && selection.every((table) => table.capacity === first.capacity);
  return {
    count,
    capacity: uniform && !allOff && first ? first.capacity : null,
    allOff,
    anyOff,
    names: selection.map((table) => table.name),
  };
}

export function planTileTone(
  table: IReservationTableRef,
  mode: 'seats' | 'groups',
  groupedIds: ReadonlySet<number>
): TPlanTileTone {
  if (!table.enabled) return 'inactive';
  if (table.reservationsMode === 'internal') return 'internal';
  if (table.reservationsMode === 'none') return 'none';
  if (table.capacity <= 0) return 'seatless';
  if (mode === 'groups' && groupedIds.has(table.id)) return 'grouped';
  return 'default';
}
